import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Send, FileText } from 'lucide-react';

const PAGE_SIZE = 25;

const HistoryTab = ({ history, colors, styles, onResendDay }) => {
  const [page, setPage] = useState(1);

  // Consolidate: group by recipient + day
  const consolidatedRows = useMemo(() => {
    const groups = {};
    history.forEach(h => {
      const phone = typeof h.recipient === 'object' ? (h.recipient?.phone || '') : (h.recipient || '');
      const key = `${phone}::${h.day}`;
      if (!groups[key]) {
        groups[key] = {
          recipient: phone,
          day: h.day,
          total: 0,
          sent: 0,
          failed: 0,
          lastDate: h.sent_at,
          isTest: h.is_test,
        };
      }
      groups[key].total++;
      if (h.status === 'sent') groups[key].sent++;
      else groups[key].failed++;
      if (new Date(h.sent_at) > new Date(groups[key].lastDate)) {
        groups[key].lastDate = h.sent_at;
      }
    });

    return Object.values(groups).sort((a, b) => {
      const ra = String(a.recipient || '');
      const rb = String(b.recipient || '');
      if (ra !== rb) return ra.localeCompare(rb);
      return a.day - b.day;
    });
  }, [history]);

  const totalPages = Math.max(1, Math.ceil(consolidatedRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = consolidatedRows.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
        fontSize: '13px', color: colors.textSecondary,
      }}>
        <FileText size={14} />
        <span>{consolidatedRows.length} registro{consolidatedRows.length !== 1 ? 's' : ''}</span>
      </div>

      {consolidatedRows.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: colors.textSecondary, fontSize: '14px',
        }}>
          Nenhum envio registrado ainda.
        </div>
      ) : (
        <div style={{
          backgroundColor: colors.bgPrimary, borderRadius: '12px',
          border: `1px solid ${colors.borderColor}`, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Destinatario
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Dia
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Msgs
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Status
                </th>
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Teste
                </th>
                <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Data
                </th>
                <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((row, i) => {
                const allSent = row.failed === 0;
                const allFailed = row.sent === 0;
                const statusLabel = allSent ? 'Enviado' : allFailed ? 'Falhou' : 'Parcial';
                const statusColor = allSent ? '#22c55e' : allFailed ? '#ef4444' : '#f59e0b';
                return (
                  <tr key={`${row.recipient}-${row.day}-${i}`} style={{ borderBottom: `1px solid ${colors.borderColor}20` }}>
                    <td style={{ padding: '10px 14px', color: colors.textPrimary, fontFamily: 'monospace', fontSize: '12px' }}>
                      {row.recipient}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary }}>
                      Dia {row.day}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textSecondary }}>
                      {row.sent}/{row.total}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                        backgroundColor: `${statusColor}20`, color: statusColor,
                      }}>
                        {statusLabel}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textSecondary }}>
                      {row.isTest ? 'Sim' : '-'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: colors.textSecondary, fontSize: '12px' }}>
                      {new Date(row.lastDate).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button
                        onClick={() => onResendDay && onResendDay(row.day, row.recipient)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'none', border: `1px solid ${colors.borderColor}`,
                          borderRadius: '6px', padding: '4px 8px',
                          color: colors.textSecondary, cursor: 'pointer',
                        }}
                        title={`Reenviar o Dia ${row.day} para este número`}
                      >
                        <Send size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '12px', marginTop: '16px',
        }}>
          <button
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === 1 ? 0.4 : 1,
              cursor: currentPage === 1 ? 'default' : 'pointer',
            }}
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <span style={{ fontSize: '13px', color: colors.textSecondary }}>
            {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === totalPages ? 0.4 : 1,
              cursor: currentPage === totalPages ? 'default' : 'pointer',
            }}
          >
            Proximo <ChevronRight size={14} />
          </button>
        </div>
      )}
    </>
  );
};

export default HistoryTab;
